import { Component, inject, Input, OnInit, signal } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { Group, GroupDatabasesInfo } from '@icure/api';
import { getDbSize } from '../../../../../lib/utils/tools-utils';
import { LocalSettingsService } from 'src/lib/services/local-settings.service';
import { EMPTY, from, map, Observable, switchMap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { DatabaseGroupInfoDatabasePasswordDialogComponent } from './database-group-info-database-password-dialog/database-group-info-database-password-dialog.component';
import { FetchService } from 'src/lib/services/fetch.service';
import { ConfigService } from 'src/lib/services/config.service';

export interface PasswordReasonDialogData {
  userEmail: string;
  reason: string;
  groupId: string;
}

const TEST_DATABASE_IDENTIFIERS: string[] = ['test', 'development'];

@Component({
  selector: 'ms-database-group-info-database',
  templateUrl: './database-group-info-database.component.html',
  styleUrls: ['./database-group-info-database.component.scss'],
  standalone: false,
})
export class DatabaseGroupInfoDatabaseComponent implements OnInit {
  @Input() groupStorageInfos: GroupDatabasesInfo = {};
  @Input() selectedGroup: Group = {};
  private localSettings = inject(LocalSettingsService);
  private firebaseAuth = inject(Auth);
  private fetchService = inject(FetchService);
  private configService = inject(ConfigService);
  readonly showPassword = signal(false);
  readonly isLoading = signal(false);
  readonly userEmail = signal('');
  readonly reason = signal('');
  readonly dialog = inject(MatDialog);

  public displayedColumns: string[] = ['id', 'docNumber', 'size', 'url', 'btn'];

  constructor() {}

  ngOnInit() {
    if (this.isTestOrDevelopmentDatabase()) {
      this.showPassword.set(true);
    }
  }

  protected isTestOrDevelopmentDatabase(): boolean {
    const groupId: string = this.selectedGroup?.id?.toLowerCase() ?? '';
    return TEST_DATABASE_IDENTIFIERS.some((identifier: string): boolean =>
      groupId.includes(identifier),
    );
  }

  public createConnectionUrl(databaseId: string): string {
    const server: string = this.selectedGroup?.servers?.[0] as string;
    return `${server}/_utils/#database/${databaseId}/_all_docs`;
  }

  public redirectToFauxton(databaseId: string): void {
    window.open(
      this.createConnectionUrl(databaseId),
      '_blank', //
    );
  }

  public copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text);
  }

  public getSize(size: number): string {
    return getDbSize(size);
  }

  openDialog(): void {
    if (this.isTestOrDevelopmentDatabase()) {
      this.showPassword.set(true);
      return;
    }

    const email = this.firebaseAuth.currentUser?.email || 'unknown';
    this.userEmail.set(email);

    const dialogRef = this.dialog.open(
      DatabaseGroupInfoDatabasePasswordDialogComponent,
      {
        data: {
          userEmail: this.userEmail(),
          reason: this.reason(),
          groupId: this.selectedGroup?.id || '',
        } as PasswordReasonDialogData,
        disableClose: false,
      },
    );

    dialogRef
      .afterClosed()
      .pipe(
        switchMap((result) => {
          if (result !== undefined && result !== '') {
            this.reason.set(result);
            this.isLoading.set(true);
            return this.logCoucDBPasswordRequest(this.reason());
          }
          this.showPassword.set(false);
          return EMPTY;
        }),
      )
      .subscribe((success) => {
        this.isLoading.set(false);
        this.showPassword.set(success);
      });
  }

  protected logCoucDBPasswordRequest(reason: string): Observable<boolean> {
    const groupId = this.selectedGroup?.id;
    if (!groupId) {
      return EMPTY;
    }

    const idToken = this.localSettings.oauthIdToken;
    if (!idToken || !reason) {
      return EMPTY;
    }

    const logEntry = {
      reason: reason,
    };

    const auditUrl = this.configService.auditServiceUrl;

    if (!auditUrl) {
      return EMPTY;
    }

    return from(
      this.fetchService.executeFetchRequest(
        `${auditUrl}/v1/couchdb/${groupId}/log-couchdb-access`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify(logEntry),
        },
      ),
    ).pipe(map((resp) => resp?.response?.status === 200));
  }
}
